import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { form } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { firstValueFrom, fromEvent, interval, Subject, Subscription } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import {
  LLM_KIRJAUSEHDOTUS_DETERMINISTISET_KATEGORIAT,
  LLM_KIRJAUSEHDOTUS_KATEGORIAT,
  LlmKategoriaprofiiliPromptRow,
  LlmKirjausehdotusAjoTulosRow,
  LlmKirjausehdotusRow,
  LlmTestisettiKokoelma,
  LlmTestitapaus,
  LlmTestitapausKategoria,
  TilikarttaRaja,
} from '../../_jaettu-lemonator/model/llm-kirjausehdotus-testaus'
import { GlobaaliPromptDialog } from './globaali-prompt.dialog'
import { callLlmTestaus } from './llm-kirjausehdotus-testaus.client'

const LLM_KATEGORIAT_UI: LlmTestitapausKategoria[] = LLM_KIRJAUSEHDOTUS_KATEGORIAT.filter((k): k is LlmTestitapausKategoria => !LLM_KIRJAUSEHDOTUS_DETERMINISTISET_KATEGORIAT.includes(k))

interface Malli {
  tarjoaja: 'claude' | 'openai'
  malli: string
}

@Component({
  templateUrl: './llm-kirjausehdotus-testaus.component.html',
  styleUrls: ['../../kirjanpito/kirjanpito.component.css', './llm-kirjausehdotus-testaus.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LlmKirjausehdotusTestausComponent implements OnInit, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()
  private _pollingSub: Subscription | null = null
  private _lataaTestiajotKaynnissa = false

  readonly LLM_KATEGORIAT_UI = LLM_KATEGORIAT_UI

  readonly globaaliPromptVersioHeader = signal<number | null>(null)

  ylatabIndex = 0

  readonly valittuKategoria = signal<LlmTestitapausKategoria | null>(LLM_KATEGORIAT_UI[0] ?? null)
  kategoriaValinta = form(signal<LlmTestitapausKategoria | null>(this.valittuKategoria()))
  readonly alivalilehti = signal<'promptti' | 'setit' | 'ajot'>('promptti')

  private readonly _ALIVALILEHDET = ['promptti', 'setit', 'ajot'] as const

  get alivalilehtiIndex(): number {
    return this._ALIVALILEHDET.indexOf(this.alivalilehti())
  }
  set alivalilehtiIndex(i: number) {
    const uusi = this._ALIVALILEHDET[i] ?? 'promptti'
    if (uusi === this.alivalilehti()) {
      return
    }
    this.alivalilehti.set(uusi)
    this._paivitaNakyvaAlivalilehti()
  }

  // Kategoriaprompti per kategoria
  readonly kategoriapromptUusin = signal<ReadonlyMap<LlmTestitapausKategoria, LlmKategoriaprofiiliPromptRow>>(new Map())
  readonly kategoriapromptMuokattu = signal<ReadonlyMap<LlmTestitapausKategoria, string>>(new Map())
  readonly kategoriapromptTilikarttaRajatMuokattu = signal<ReadonlyMap<LlmTestitapausKategoria, TilikarttaRaja[]>>(new Map())
  readonly kategoriapromptLataa = signal<ReadonlySet<LlmTestitapausKategoria>>(new Set())
  readonly kategoriapromptTallennetaan = signal<ReadonlySet<LlmTestitapausKategoria>>(new Set())
  readonly kategoriapromptHistoria = signal<ReadonlyMap<LlmTestitapausKategoria, LlmKategoriaprofiiliPromptRow[]>>(new Map())
  readonly naytaKategoriapromptHistoriaJoukko = signal<ReadonlySet<LlmTestitapausKategoria>>(new Set())
  readonly esikatseluKategoriaVersio = signal<ReadonlyMap<LlmTestitapausKategoria, LlmKategoriaprofiiliPromptRow | null>>(new Map())

  // Setit
  readonly setit = signal<ReadonlyMap<LlmTestitapausKategoria, LlmTestisettiKokoelma[]>>(new Map())
  readonly setitLataa = signal<ReadonlySet<LlmTestitapausKategoria>>(new Set())
  readonly uusiSettiNimi = signal('')
  readonly luoSettiKaynnissa = signal(false)

  readonly valittuSetti = signal<LlmTestisettiKokoelma | null>(null)
  readonly settiTestitapaukset = signal<LlmTestitapaus[]>([])
  readonly settiTestitapauksetLataa = signal(false)
  readonly poistetaanJasenyys = signal<ReadonlySet<string>>(new Set())

  // Testiajot
  readonly testiajot = signal<ReadonlyMap<LlmTestitapausKategoria, LlmKirjausehdotusAjoTulosRow[]>>(new Map())
  readonly testiajotLataa = signal<ReadonlySet<LlmTestitapausKategoria>>(new Set())

  readonly mallit: Malli[] = [
    { tarjoaja: 'claude', malli: 'claude-opus-4-8' },
    { tarjoaja: 'claude', malli: 'claude-sonnet-4-6' },
    { tarjoaja: 'claude', malli: 'claude-haiku-4-5' },
  ]
  valittuMalli: Malli = this.mallit.find(m => m.malli === 'claude-haiku-4-5') ?? this.mallit[0]
  valittuAjoSetti: LlmTestisettiKokoelma | null = null
  readonly testiajoKaynnissa = signal(false)

  // Tulokset (avattu ajo)
  readonly tuloksetTestiajoAvain = signal<string | null>(null)
  readonly tulokset = signal<LlmKirjausehdotusRow[]>([])
  readonly tuloksetLataa = signal(false)
  readonly naytaPrompttiAvaimet = signal<ReadonlySet<string>>(new Set())
  readonly kasiteltyLkm = signal<ReadonlyMap<string, number>>(new Map())

  constructor(
    private _firebaseLemonator: FirebaseLemonator,
    private _snackbar: MatSnackBar,
    private _dialog: MatDialog,
  ) {}

  ngOnInit() {
    this._lataaGlobaaliPromptVersio()
    if (this.valittuKategoria()) {
      this._valitseKategoria(this.valittuKategoria())
    }
    fromEvent(document, 'visibilitychange')
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => {
        if (document.visibilityState === 'visible') {
          this._paivitaNakyvaAlivalilehti()
        }
      })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
    this._pollingSub?.unsubscribe()
  }

  vertaileKategoriat(a: LlmTestitapausKategoria | null, b: LlmTestitapausKategoria | null): boolean {
    return a === b
  }

  // ---------------------------------------------------------------------------
  // Globaali prompt
  // ---------------------------------------------------------------------------

  private async _lataaGlobaaliPromptVersio() {
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'haeGlobaaliPromptUusin', {})
      if (vastaus.e || !vastaus.rivi) {
        return
      }
      this.globaaliPromptVersioHeader.set(vastaus.rivi.versio)
    } catch (err) {
      console.error('_lataaGlobaaliPromptVersio virhe', err)
    }
  }

  avaaGlobaaliPromptDialog() {
    const ref = this._dialog.open(GlobaaliPromptDialog, {
      panelClass: 'globaali-prompt-dialog',
      autoFocus: false,
    })
    ref.afterClosed().subscribe(() => this._lataaGlobaaliPromptVersio())
  }

  // ---------------------------------------------------------------------------
  // Kategoria-valinta + lazy lataus
  // ---------------------------------------------------------------------------

  valitseKategoria(kategoria: LlmTestitapausKategoria) {
    this._valitseKategoria(kategoria)
  }

  private async _valitseKategoria(kategoria: LlmTestitapausKategoria) {
    this.valittuKategoria.set(kategoria)
    asetaArvo(this.kategoriaValinta, kategoria)
    this.valittuSetti.set(null)
    this.settiTestitapaukset.set([])
    if (!this.kategoriapromptUusin().has(kategoria)) {
      this._lataaKategoriaprompt(kategoria)
    }
    if (!this.setit().has(kategoria)) {
      this._lataaSetit(kategoria)
    }
    if (!this.testiajot().has(kategoria)) {
      this._lataaTestiajot(kategoria)
    }
  }

  private _paivitaNakyvaAlivalilehti() {
    const kategoria = this.valittuKategoria()
    if (!kategoria) {
      return
    }
    if (this.alivalilehti() === 'setit') {
      this._lataaSetit(kategoria)
    } else if (this.alivalilehti() === 'ajot') {
      this._lataaTestiajot(kategoria, false)
    } else if (this.alivalilehti() === 'promptti') {
      // Älä ylikirjoita käyttäjän tallentamattomia muokkauksia (prompt tai tilikarttarajat).
      const uusin = this.kategoriapromptUusin().get(kategoria)
      const muokattuPrompt = this.kategoriapromptMuokattu().get(kategoria)
      const muokatutRajat = this.kategoriapromptTilikarttaRajatMuokattu().get(kategoria)
      const promptOk = muokattuPrompt === undefined || muokattuPrompt === uusin?.prompt
      const rajatOk = muokatutRajat === undefined || JSON.stringify(muokatutRajat) === JSON.stringify(uusin?.tilikarttaRajat ?? [])
      if (promptOk && rajatOk) {
        this._lataaKategoriaprompt(kategoria)
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Kategoriaprofiilin prompt
  // ---------------------------------------------------------------------------

  private async _lataaKategoriaprompt(kategoria: LlmTestitapausKategoria) {
    this.kategoriapromptLataa.update(joukko => new Set(joukko).add(kategoria))
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'haeKategoriaprofiilinPromptUusin', { kategoria })
      if (vastaus.e || !vastaus.rivi) {
        return
      }
      this.kategoriapromptUusin.update(kokoelma => new Map(kokoelma).set(kategoria, vastaus.rivi))
      this.kategoriapromptMuokattu.update(kokoelma => new Map(kokoelma).set(kategoria, vastaus.rivi.prompt))
      this.kategoriapromptTilikarttaRajatMuokattu.update(kokoelma =>
        new Map(kokoelma).set(
          kategoria,
          vastaus.rivi.tilikarttaRajat.map(raja => ({ ...raja })),
        ),
      )
    } catch (err) {
      console.error('_lataaKategoriaprompt virhe', err)
    } finally {
      this.kategoriapromptLataa.update(joukko => new Set([...joukko].filter(arvo => arvo !== kategoria)))
    }
  }

  asetaKategoriapromptMuokattu(kategoria: LlmTestitapausKategoria, arvo: string) {
    this.kategoriapromptMuokattu.update(kokoelma => new Map(kokoelma).set(kategoria, arvo))
  }

  asetaTilikarttaRajat(kategoria: LlmTestitapausKategoria, rajat: TilikarttaRaja[]) {
    this.kategoriapromptTilikarttaRajatMuokattu.update(kokoelma => new Map(kokoelma).set(kategoria, rajat))
  }

  onkoTilikarttarajaKelvollinen(kategoria: LlmTestitapausKategoria): boolean {
    const rajat = this.kategoriapromptTilikarttaRajatMuokattu().get(kategoria) ?? []
    return rajat.every(
      raja => Number.isInteger(raja.alkaa) && Number.isInteger(raja.loppuu) && raja.alkaa >= 0 && raja.alkaa <= 9999 && raja.loppuu >= 0 && raja.loppuu <= 9999 && raja.alkaa <= raja.loppuu,
    )
  }

  async tallennaKategoriaprompt(kategoria: LlmTestitapausKategoria) {
    const prompt = this.kategoriapromptMuokattu().get(kategoria) ?? ''
    const tilikarttaRajat = this.kategoriapromptTilikarttaRajatMuokattu().get(kategoria) ?? []
    if (!this.onkoTilikarttarajaKelvollinen(kategoria)) {
      this._snackbar.open('Tarkista tilisarjat: jokaisessa sarjassa anna 0–9999 ja varmista että alkaa ≤ loppuu', 'Sulje', { duration: 4000 })
      return
    }
    this.kategoriapromptTallennetaan.update(joukko => new Set(joukko).add(kategoria))
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'tallennaKategoriaprofiilinPrompt', { kategoria, prompt, tilikarttaRajat })
      if (vastaus.e || vastaus.versio == null) {
        this._snackbar.open('Tallennus epäonnistui', 'Sulje', { duration: 4000 })
        return
      }
      this._snackbar.open(`Kategoriaprompti tallennettu (v${vastaus.versio})`, 'Sulje', { duration: 3000 })
      await this._lataaKategoriaprompt(kategoria)
      await this._lataaKategoriapromptHistoria(kategoria)
    } catch (err) {
      console.error('tallennaKategoriaprompt virhe', err)
      this._snackbar.open('Tallennus epäonnistui', 'Sulje', { duration: 4000 })
    } finally {
      this.kategoriapromptTallennetaan.update(joukko => new Set([...joukko].filter(arvo => arvo !== kategoria)))
    }
  }

  async toggleKategoriapromptHistoria(kategoria: LlmTestitapausKategoria) {
    if (this.naytaKategoriapromptHistoriaJoukko().has(kategoria)) {
      this.naytaKategoriapromptHistoriaJoukko.update(joukko => new Set([...joukko].filter(arvo => arvo !== kategoria)))
      return
    }
    this.naytaKategoriapromptHistoriaJoukko.update(joukko => new Set(joukko).add(kategoria))
    if (!this.kategoriapromptHistoria().has(kategoria)) {
      await this._lataaKategoriapromptHistoria(kategoria)
    }
  }

  private async _lataaKategoriapromptHistoria(kategoria: LlmTestitapausKategoria) {
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'haeKategoriaprofiilinPromptHistoria', { kategoria })
      this.kategoriapromptHistoria.update(kokoelma => new Map(kokoelma).set(kategoria, vastaus.rivit ?? []))
    } catch (err) {
      console.error('_lataaKategoriapromptHistoria virhe', err)
    }
  }

  esikatseleKategoriaVersio(kategoria: LlmTestitapausKategoria, versio: LlmKategoriaprofiiliPromptRow) {
    this.esikatseluKategoriaVersio.update(kokoelma => new Map(kokoelma).set(kategoria, versio))
  }

  suljeEsikatseluKategoria(kategoria: LlmTestitapausKategoria) {
    this.esikatseluKategoriaVersio.update(kokoelma => new Map(kokoelma).set(kategoria, null))
  }

  private async _vahvista(text: string, header: string, rightAction: string): Promise<boolean> {
    const data: AreYouSureDialogData = { text, header, rightAction }
    return !!(await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed()))
  }

  async palautaKategoriaVersio(kategoria: LlmTestitapausKategoria, versio: number) {
    if (!(await this._vahvista(`Palauta kategorian "${kategoria}" prompti versiosta ${versio}? Toiminto luo uuden version, jonka teksti on valitun version teksti.`, 'Palauta versio', 'Palauta'))) {
      return
    }
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'palautaKategoriaprofiilinPromptVersio', { kategoria, versio })
      if (vastaus.e || vastaus.versio == null) {
        this._snackbar.open('Palautus epäonnistui', 'Sulje', { duration: 4000 })
        return
      }
      this._snackbar.open(`Versio ${versio} palautettu uutena versiona v${vastaus.versio}`, 'Sulje', { duration: 3000 })
      this.esikatseluKategoriaVersio.update(kokoelma => new Map(kokoelma).set(kategoria, null))
      await this._lataaKategoriaprompt(kategoria)
      await this._lataaKategoriapromptHistoria(kategoria)
    } catch (err) {
      console.error('palautaKategoriaVersio virhe', err)
      this._snackbar.open('Palautus epäonnistui', 'Sulje', { duration: 4000 })
    }
  }

  // ---------------------------------------------------------------------------
  // Setit
  // ---------------------------------------------------------------------------

  private async _lataaSetit(kategoria: LlmTestitapausKategoria) {
    this.setitLataa.update(joukko => new Set(joukko).add(kategoria))
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'haeSetit', { kategoria })
      this.setit.update(kokoelma => new Map(kokoelma).set(kategoria, vastaus.setit ?? []))
    } catch (err) {
      console.error('_lataaSetit virhe', err)
    } finally {
      this.setitLataa.update(joukko => new Set([...joukko].filter(arvo => arvo !== kategoria)))
    }
  }

  async luoUusiSetti() {
    if (!this.valittuKategoria() || !this.uusiSettiNimi().trim()) {
      this._snackbar.open('Anna setille nimi', 'Sulje', { duration: 4000 })
      return
    }
    this.luoSettiKaynnissa.set(true)
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'luoSetti', { kategoria: this.valittuKategoria(), nimi: this.uusiSettiNimi().trim() })
      if (vastaus.e || !vastaus.avain) {
        this._snackbar.open('Setin luonti epäonnistui', 'Sulje', { duration: 4000 })
        return
      }
      this._snackbar.open('Setti luotu', 'Sulje', { duration: 3000 })
      this.uusiSettiNimi.set('')
      await this._lataaSetit(this.valittuKategoria())
    } catch (err) {
      console.error('luoUusiSetti virhe', err)
      this._snackbar.open('Setin luonti epäonnistui', 'Sulje', { duration: 4000 })
    } finally {
      this.luoSettiKaynnissa.set(false)
    }
  }

  async poistaSetti(setti: LlmTestisettiKokoelma) {
    if (!(await this._vahvista(`Poista setti "${setti.nimi}"? Tätä ei voi peruuttaa.`, 'Poista setti', 'Poista'))) {
      return
    }
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'poistaSetti', { avain: setti.avain })
      if (vastaus.e) {
        this._snackbar.open('Poisto epäonnistui', 'Sulje', { duration: 4000 })
        return
      }
      if (this.valittuSetti()?.avain === setti.avain) {
        this.valittuSetti.set(null)
      }
      if (this.valittuKategoria()) {
        await this._lataaSetit(this.valittuKategoria())
      }
    } catch (err) {
      console.error('poistaSetti virhe', err)
      this._snackbar.open('Poisto epäonnistui', 'Sulje', { duration: 4000 })
    }
  }

  async avaaSetti(setti: LlmTestisettiKokoelma) {
    this.valittuSetti.set(setti)
    await this._lataaSettiTestitapaukset(setti)
    const kategoria = this.valittuKategoria()
    const kategorianSetit = kategoria ? this.setit().get(kategoria) : undefined
    if (kategorianSetit?.some(s => s.avain === setti.avain)) {
      const testitapausLkm = this.settiTestitapaukset().length
      this.setit.update(kokoelma =>
        new Map(kokoelma).set(
          kategoria,
          kategorianSetit.map(s => (s.avain === setti.avain ? { ...s, testitapausLkm } : s)),
        ),
      )
    }
  }

  suljeSetti() {
    this.valittuSetti.set(null)
    this.settiTestitapaukset.set([])
    if (this.valittuKategoria()) {
      this._lataaSetit(this.valittuKategoria())
    }
  }

  private async _lataaSettiTestitapaukset(setti: LlmTestisettiKokoelma) {
    this.settiTestitapauksetLataa.set(true)
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'haeTestisetti', { settiAvain: setti.avain })
      this.settiTestitapaukset.set(vastaus.rivit ?? [])
    } catch (err) {
      console.error('_lataaSettiTestitapaukset virhe', err)
    } finally {
      this.settiTestitapauksetLataa.set(false)
    }
  }

  async poistaJasenyys(testitapaus: LlmTestitapaus) {
    if (!this.valittuSetti()) {
      return
    }
    this.poistetaanJasenyys.update(joukko => new Set(joukko).add(testitapaus.avain))
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'poistaSettiJasenyys', { settiAvain: this.valittuSetti().avain, testitapausAvain: testitapaus.avain })
      if (vastaus.e) {
        this._snackbar.open('Poisto epäonnistui', 'Sulje', { duration: 4000 })
        return
      }
      this.settiTestitapaukset.set(this.settiTestitapaukset().filter(t => t.avain !== testitapaus.avain))
      if (this.valittuKategoria()) {
        await this._lataaSetit(this.valittuKategoria())
      }
    } catch (err) {
      console.error('poistaJasenyys virhe', err)
      this._snackbar.open('Poisto epäonnistui', 'Sulje', { duration: 4000 })
    } finally {
      this.poistetaanJasenyys.update(joukko => new Set([...joukko].filter(arvo => arvo !== testitapaus.avain)))
    }
  }

  // ---------------------------------------------------------------------------
  // Testiajot
  // ---------------------------------------------------------------------------

  private async _lataaTestiajot(kategoria: LlmTestitapausKategoria, naytaSpinneri = true) {
    if (this._lataaTestiajotKaynnissa) {
      return
    }
    this._lataaTestiajotKaynnissa = true
    if (naytaSpinneri) {
      this.testiajotLataa.update(joukko => new Set(joukko).add(kategoria))
    }
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'haeTestiajot', { kategoria })
      const ajot = vastaus.kirjausehdotusAjot ?? []
      this.testiajot.update(kokoelma => new Map(kokoelma).set(kategoria, ajot))
      const kaynnissa = ajot.filter(t => t.tila === 'kaynnissa')
      if (kaynnissa.length) {
        this._aloitaPolling()
        const edistymiset = await Promise.all(
          kaynnissa.map(t => callLlmTestaus(this._firebaseLemonator, 'haeTestiajonEdistyminen', { kirjausehdotusAjoAvain: t.avain }).then(v => ({ avain: t.avain, lkm: v.kirjausehdotustenLkm ?? 0 }))),
        )
        this.kasiteltyLkm.update(lkmt => {
          const uudet = new Map(lkmt)
          for (const { avain, lkm } of edistymiset) {
            uudet.set(avain, lkm)
          }
          return uudet
        })
      } else {
        this._lopetaPolling()
      }
    } catch (err) {
      console.error('_lataaTestiajot virhe', err)
    } finally {
      this._lataaTestiajotKaynnissa = false
      if (naytaSpinneri) {
        this.testiajotLataa.update(joukko => new Set([...joukko].filter(arvo => arvo !== kategoria)))
      }
    }
  }

  async aloitaTestiAjo() {
    if (!this.valittuAjoSetti) {
      this._snackbar.open('Valitse setti', 'Sulje', { duration: 4000 })
      return
    }
    this.testiajoKaynnissa.set(true)
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'aloitaTestiAjo', {
        testisettiAvain: this.valittuAjoSetti.avain,
        tarjoaja: this.valittuMalli.tarjoaja,
        malli: this.valittuMalli.malli,
      })
      if (vastaus.e === 'no-globaali-prompt' || vastaus.e === 'no-kategoria-prompt') {
        this._snackbar.open('Globaali tai kategoriaprompti puuttuu', 'Sulje', { duration: 4000 })
        return
      }
      if (vastaus.e === 'no-testitapauksia') {
        this._snackbar.open('Setti on tyhjä', 'Sulje', { duration: 4000 })
        return
      }
      if (vastaus.e || !vastaus.kirjausehdotusAjoAvain) {
        this._snackbar.open('Testiajoa ei voitu aloittaa', 'Sulje', { duration: 4000 })
        return
      }
      this._snackbar.open('Testiajo aloitettu', 'Sulje', { duration: 3000 })
      this.alivalilehti.set('ajot')
      if (this.valittuKategoria()) {
        await this._lataaTestiajot(this.valittuKategoria())
      }
    } catch (err) {
      console.error('aloitaTestiAjo virhe', err)
      this._snackbar.open('Testiajoa ei voitu aloittaa', 'Sulje', { duration: 4000 })
    } finally {
      this.testiajoKaynnissa.set(false)
    }
  }

  onTestiajoPaneelAuki(testiajo: LlmKirjausehdotusAjoTulosRow) {
    if (testiajo.tila === 'kaynnissa') {
      return
    }
    if (this.tuloksetTestiajoAvain() === testiajo.avain) {
      return
    }
    this._haeTulokset(testiajo.avain)
  }

  onTestiajoPaneelKiinni(avain: string) {
    if (this.tuloksetTestiajoAvain() === avain) {
      this.tuloksetTestiajoAvain.set(null)
      this.tulokset.set([])
    }
    this.naytaPrompttiAvaimet.update(joukko => new Set([...joukko].filter(arvo => arvo !== avain)))
  }

  togglePrompt(avain: string) {
    if (this.naytaPrompttiAvaimet().has(avain)) {
      this.naytaPrompttiAvaimet.update(joukko => new Set([...joukko].filter(arvo => arvo !== avain)))
    } else {
      this.naytaPrompttiAvaimet.update(joukko => new Set(joukko).add(avain))
    }
  }

  private async _haeTulokset(testiajoAvain: string) {
    this.tuloksetTestiajoAvain.set(testiajoAvain)
    this.tuloksetLataa.set(true)
    try {
      const vastaus = await callLlmTestaus(this._firebaseLemonator, 'haeTulokset', { kirjausehdotusAjoAvain: testiajoAvain })
      this.tulokset.set(vastaus.kirjausehdotukset ?? [])
    } catch (err) {
      console.error('_haeTulokset virhe', err)
    } finally {
      this.tuloksetLataa.set(false)
    }
  }

  private _aloitaPolling() {
    if (this._pollingSub) {
      return
    }
    this._pollingSub = interval(15000)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => {
        if (this.valittuKategoria()) {
          this._lataaTestiajot(this.valittuKategoria(), false)
        }
      })
  }

  private _lopetaPolling() {
    if (this._pollingSub) {
      this._pollingSub.unsubscribe()
      this._pollingSub = null
    }
  }
}
